import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  inject,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PopoverAnchorDirective,
  PopoverBodyTemplateDirective,
  PopoverComponent,
  PopoverTitleTemplateDirective,
  TooltipDirective
} from '@progress/kendo-angular-tooltip';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';
import { NotificationDto } from 'src/app/models/notification/notification-dto';
import { NotificationModuleType } from 'src/app/models/notification/notification-module-type';
import {
  NotificationStatisticsDto,
  NotificationStatisticsSliceDto
} from 'src/app/models/notification/notification-statistics-dto';
import { SignalBaseDto } from 'src/app/models/signal/signal-base-dto';
import { SignalType } from 'src/app/models/signal/signal-type';
import { NotificationsService } from 'src/app/services/api/notification-api/notifications.service';
import { NotificationBadgeService } from 'src/app/services/local';
import { NotificationNavigationService } from 'src/app/services/local/navigation/notification-navigation.service';
import { ModalOpenerService } from 'src/app/services/modal-opener.service';
import { SignalDispatcherService } from 'src/app/services/signal/signal-dispatcher.service';
import { NotificationWorkItemNavigationEvent } from '../../model/notification-work-item-navigation-event';
import { NotificationsListWorkItemsComponent } from '../notifications-list-work-items/notifications-list-work-items.component';
import { notificationsSetupModalOpener } from '../notifications-setup-modal/notifications-setup-modal.modal';

@Component({
  selector: 'app-notifications-bell',
  templateUrl: './notifications-bell.component.html',
  styleUrls: ['./notifications-bell.component.scss'],
  imports: [
    PopoverAnchorDirective,
    PopoverComponent,
    PopoverTitleTemplateDirective,
    TooltipDirective,
    PopoverBodyTemplateDirective,
    NotificationsListWorkItemsComponent
  ]
})
export class NotificationsBellComponent implements OnInit, OnDestroy {
  private readonly modalOpener = inject(ModalOpenerService);
  private readonly notificationService = inject(NotificationsService);
  private readonly notificationNavigationService = inject(
    NotificationNavigationService
  );
  private readonly notificationBadgeService = inject(NotificationBadgeService);
  private readonly signalDispatcherService = inject(SignalDispatcherService);
  private readonly destroyRef = inject(DestroyRef);

  readonly anchor = viewChild.required<PopoverAnchorDirective>('anchor');

  signalDispatcherSub: Subscription;

  statistics: NotificationStatisticsDto;

  notifications: NotificationDto[];

  ngOnInit(): void {
    this.loadNotificationStatistics();

    this.signalDispatcherSub = this.signalDispatcherService.subscribe(
      (signal: SignalBaseDto) => {
        if (signal.type == SignalType.Notification) {
          this.loadNotificationStatistics();
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.signalDispatcherSub) {
      this.signalDispatcherSub.unsubscribe();
    }
  }

  get hasUnreadNotifications(): boolean {
    return this.statistics?.totalUnread > 0;
  }

  get unreadNotifications(): number {
    return this.statistics?.totalUnread ?? 0;
  }

  get workItemsStatisticsSlice(): NotificationStatisticsSliceDto {
    const result =
      this.statistics?.slices?.find(
        slice => slice.module == NotificationModuleType.WorkItem
      ) ??
      NotificationStatisticsSliceDto.create(NotificationModuleType.WorkItem);
    return result;
  }

  canDisplayNotificationsSetup(): boolean {
    return true;
  }

  onBellButtonClick(_event: MouseEvent): void {
    this.loadNotificationStatistics();
  }

  displayNotificationsSetup(): void {
    this.modalOpener.open(notificationsSetupModalOpener);
    this.hidePopover();
  }

  navigateToWorkItems(event: NotificationWorkItemNavigationEvent): void {
    this.notificationNavigationService.navigateToNewTab(event.workItemIds);
  }

  reloadStatistics(): void {
    this.loadNotificationStatistics();
  }

  private loadNotificationStatistics(): void {
    const date = dayjs().subtract(20, 'day');
    this.notificationService
      .getStatistics$(date)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: NotificationStatisticsDto) => {
        this.statistics = result;
        if (result?.totalUnread > 0) {
          this.notificationBadgeService.updateFavicon(result?.totalUnread);
        } else {
          this.notificationBadgeService.resetFavicon();
        }
      });
  }

  private hidePopover(): void {
    this.anchor().hide();
  }
}
